/**
 * QueueHealthChart.tsx - Queue health time-series visualization
 *
 * S_031_C_004: Displays historical queue health data for trend analysis
 *
 * Design: Light theme matching existing admin cards styling.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Paper,
  Group,
  Stack,
  Text,
  SegmentedControl,
  Alert,
  Skeleton,
  Box,
  Tooltip,
  ActionIcon,
  ThemeIcon
} from '@mantine/core';
import {
  IconRefresh,
  IconChartLine,
  IconAlertTriangle,
  IconClock
} from '@tabler/icons-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { adminOperationsRepository } from '../../../../infrastructure/repositories/AdminOperationsRepository';
import type { QueueHealthHistoryDto, QueueHealthDataPoint } from '../../../../domain/entities/Region';

// ============================================================================
// Types
// ============================================================================

interface QueueHealthChartProps {
  regionId: string;
}

interface ChartDataPoint {
  timestamp: string;
  formattedTime: string;
  matchQueueDepth: number;
  drillingBacklogDepth: number;
}

// ============================================================================
// Time Range Options
// ============================================================================

const TIME_RANGE_OPTIONS = [
  { label: '1h', value: '1' },
  { label: '6h', value: '6' },
  { label: '24h', value: '24' },
  { label: '7d', value: '168' }
];

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Format timestamp for X-axis display
 */
function formatTimestamp(isoString: string, hours: number): string {
  const date = new Date(isoString);

  // For 7 days, show date + time
  if (hours >= 168) {
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  // For 24 hours, show hour:minute
  if (hours >= 24) {
    return date.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  // For shorter ranges, show hour:minute
  return date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit'
  });
}

/**
 * Format number with K/M abbreviations
 */
function formatNumber(value: number): string {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`;
  }
  return value.toLocaleString();
}

// ============================================================================
// Custom Tooltip Component
// ============================================================================

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
    name: string;
    color: string;
  }>;
  label?: string;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({
  active,
  payload,
  label
}): React.ReactElement | null => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  return (
    <Paper
      p="sm"
      radius="sm"
      shadow="md"
      withBorder
    >
      <Text size="xs" c="dimmed" mb={4}>
        {label}
      </Text>
      <Stack gap={4}>
        {payload.map((entry, index) => (
          <Group key={index} gap="xs">
            <Box
              style={{
                width: 10,
                height: 10,
                borderRadius: 2,
                backgroundColor: entry.color
              }}
            />
            <Text size="xs" c="dimmed">
              {entry.name}:
            </Text>
            <Text
              size="xs"
              fw={600}
              style={{ fontFamily: 'var(--mantine-font-family-monospace)' }}
            >
              {formatNumber(entry.value)}
            </Text>
          </Group>
        ))}
      </Stack>
    </Paper>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const QueueHealthChart: React.FC<QueueHealthChartProps> = ({
  regionId
}): React.ReactElement => {
  const [hours, setHours] = useState<string>('24');
  const [historyData, setHistoryData] = useState<QueueHealthHistoryDto | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch queue health history data
   */
  const fetchHistory = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await adminOperationsRepository.getQueueHealthHistory(
        regionId,
        parseInt(hours, 10)
      );
      setHistoryData(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load queue health history';
      setError(errorMessage);
      setHistoryData(null);
    } finally {
      setIsLoading(false);
    }
  }, [regionId, hours]);

  /**
   * Fetch data when regionId or hours change
   */
  useEffect(() => {
    void fetchHistory();
  }, [fetchHistory]);

  /**
   * Transform data for Recharts
   */
  const chartData: ChartDataPoint[] = useMemo(() => {
    if (!historyData || !historyData.dataPoints) {
      return [];
    }

    const hoursNum = parseInt(hours, 10);

    return historyData.dataPoints.map((point: QueueHealthDataPoint) => ({
      timestamp: point.timestamp,
      formattedTime: formatTimestamp(point.timestamp, hoursNum),
      matchQueueDepth: point.matchQueueDepth,
      drillingBacklogDepth: point.drillingBacklogDepth
    }));
  }, [historyData, hours]);

  /**
   * Calculate Y-axis domain
   */
  const yAxisDomain = useMemo((): [number, number] => {
    if (chartData.length === 0) {
      return [0, 100];
    }

    const maxMatch = Math.max(...chartData.map(d => d.matchQueueDepth));
    const maxDrilling = Math.max(...chartData.map(d => d.drillingBacklogDepth));
    const maxValue = Math.max(maxMatch, maxDrilling, 100);

    // Round up to nice number
    const magnitude = Math.pow(10, Math.floor(Math.log10(maxValue)));
    const rounded = Math.ceil(maxValue / magnitude) * magnitude;

    return [0, rounded];
  }, [chartData]);

  /**
   * Handle time range change
   */
  const handleHoursChange = useCallback((value: string): void => {
    setHours(value);
  }, []);

  /**
   * Render loading state
   */
  if (isLoading && !historyData) {
    return (
      <Paper shadow="sm" p="md" radius="md" withBorder>
        <Stack gap="md">
          <Skeleton height={40} radius="sm" />
          <Skeleton height={300} radius="sm" />
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper shadow="sm" p="md" radius="md" withBorder>
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between" align="center">
          <Group gap="xs">
            <ThemeIcon size={32} radius="md" variant="light" color="blue">
              <IconChartLine size={18} />
            </ThemeIcon>
            <Text size="sm" fw={600}>
              Queue Health History
            </Text>
          </Group>

          <Group gap="md">
            <Group gap="xs">
              <IconClock size={14} color="var(--mantine-color-gray-6)" />
              <SegmentedControl
                size="xs"
                value={hours}
                onChange={handleHoursChange}
                data={TIME_RANGE_OPTIONS}
              />
            </Group>
            <Tooltip label="Refresh">
              <ActionIcon
                variant="light"
                color="blue"
                size="sm"
                onClick={(): void => void fetchHistory()}
                loading={isLoading}
              >
                <IconRefresh size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>

        {/* Error State */}
        {error && (
          <Alert
            icon={<IconAlertTriangle size={16} />}
            title="Error"
            color="red"
            variant="light"
          >
            {error}
          </Alert>
        )}

        {/* No Data State */}
        {!error && chartData.length === 0 && !isLoading && (
          <Box
            style={{
              height: 250,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'var(--mantine-color-gray-0)',
              borderRadius: 'var(--mantine-radius-md)'
            }}
          >
            <Stack align="center" gap="xs">
              <ThemeIcon size={48} radius="xl" variant="light" color="gray">
                <IconChartLine size={24} />
              </ThemeIcon>
              <Text size="sm" c="dimmed" ta="center" fw={500}>
                No history data available yet
              </Text>
              <Text size="xs" c="dimmed" ta="center">
                Snapshots are captured every 15 minutes.
                <br />
                Check back soon to see queue trends.
              </Text>
            </Stack>
          </Box>
        )}

        {/* Chart */}
        {chartData.length > 0 && (
          <Box style={{ width: '100%', height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chartData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="var(--mantine-color-gray-3)"
                />
                <XAxis
                  dataKey="formattedTime"
                  stroke="var(--mantine-color-gray-5)"
                  tick={{ fill: 'var(--mantine-color-gray-7)', fontSize: 11 }}
                  tickLine={{ stroke: 'var(--mantine-color-gray-4)' }}
                />
                <YAxis
                  domain={yAxisDomain}
                  stroke="var(--mantine-color-gray-5)"
                  tick={{ fill: 'var(--mantine-color-gray-7)', fontSize: 11 }}
                  tickLine={{ stroke: 'var(--mantine-color-gray-4)' }}
                  tickFormatter={(value: number): string => formatNumber(value)}
                />
                <RechartsTooltip
                  content={<CustomTooltip />}
                  labelFormatter={(label: string): string => {
                    // Find the original timestamp
                    const point = chartData.find(d => d.formattedTime === label);
                    if (point) {
                      return new Date(point.timestamp).toLocaleString();
                    }
                    return label;
                  }}
                />
                <Legend
                  wrapperStyle={{
                    paddingTop: '10px'
                  }}
                  formatter={(value: string): React.ReactElement => (
                    <Text component="span" size="xs">
                      {value}
                    </Text>
                  )}
                />
                <Line
                  type="monotone"
                  dataKey="matchQueueDepth"
                  name="Match Queue"
                  stroke="var(--mantine-color-blue-6)"
                  strokeWidth={2}
                  dot={chartData.length < 50}
                  activeDot={{ r: 6, fill: 'var(--mantine-color-blue-6)' }}
                />
                <Line
                  type="monotone"
                  dataKey="drillingBacklogDepth"
                  name="Drilling Backlog"
                  stroke="var(--mantine-color-orange-6)"
                  strokeWidth={2}
                  dot={chartData.length < 50}
                  activeDot={{ r: 6, fill: 'var(--mantine-color-orange-6)' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        )}

        {/* Footer with metadata */}
        {historyData && (
          <Group justify="space-between">
            <Text size="xs" c="dimmed">
              Region: {historyData.regionName}
            </Text>
            <Text size="xs" c="dimmed">
              {chartData.length} data point{chartData.length !== 1 ? 's' : ''}
            </Text>
          </Group>
        )}
      </Stack>
    </Paper>
  );
};

export default QueueHealthChart;
